import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FileText } from 'lucide-react';
import { formatDateBr, formatDateTimeBr } from '../../../utils/formatters/dateUtils';
import { useReportConfig } from '../../../contexts/ReportConfigContext.jsx';
import ReportStyledField from '../ReportStyledField.jsx';

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const formatAreaHa = (value, decimals = 2) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ha`;
};

const formatCurrencyBr = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const formatDocument = (value) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return String(value);
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return String(value);
};

const getStatusChipColor = (status) => {
  if (!status) return 'default';
  const normalized = String(status).toLowerCase();
  if (
    normalized.includes('quitado') ||
    normalized.includes('válida') ||
    normalized.includes('valida')
  ) {
    return 'success';
  }
  if (normalized.includes('pendente') || normalized.includes('vencido')) {
    return 'warning';
  }
  return 'default';
};

const normalizeCcirData = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.filter((item) => item && typeof item === 'object');
  }

  if (typeof raw === 'object') {
    return [raw];
  }

  return [];
};

export default function CcirDomain({ data }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  const records = useMemo(() => normalizeCcirData(data), [data]);

  if (!isDomainEnabled('serpro_ccir')) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          CCIR
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Domínio CCIR desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!records.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          CCIR
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro CCIR encontrado.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FileText size={20} style={{ marginRight: 8 }} />
        <Typography variant="h6">CCIR - Cadastro de Imóvel Rural</Typography>
      </Box>

      {records.map((ccir, index) => {
        const areasRegistradas = Array.isArray(ccir.areas_registradas)
          ? ccir.areas_registradas.filter((a) => a && typeof a === 'object')
          : [];

        const titulares = Array.isArray(ccir.titulares)
          ? ccir.titulares.filter((t) => t && typeof t === 'object')
          : [];

        const dadosUltimoCcir =
          ccir.dados_ultimo_ccir && typeof ccir.dados_ultimo_ccir === 'object'
            ? ccir.dados_ultimo_ccir
            : null;

        const codigoIncra = ccir.codigo_imovel_incra;

        return (
          <Card
            key={codigoIncra || index}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'primary.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              {isSectionEnabled('serpro_ccir', 'dados_imovel') && (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Informações do Imóvel
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'codigo_imovel_incra') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('serpro_ccir', 'codigo_imovel_incra', 'Código INCRA')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                          <ReportStyledField
                            domainKey="serpro_ccir"
                            fieldKey="codigo_imovel_incra"
                            variant="body2"
                            defaultSx={{ fontWeight: 500, wordBreak: 'break-all' }}
                          >
                            {formatGeneric(codigoIncra)}
                          </ReportStyledField>
                          {codigoIncra && (
                            <IconButton
                              size="small"
                              aria-label="Copiar Código INCRA CCIR"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(String(codigoIncra)).catch(() => {});
                              }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'denominacao') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('serpro_ccir', 'denominacao', 'Denominação')}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="denominacao"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(ccir.denominacao)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'classificacao_fundiaria') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel(
                            'serpro_ccir',
                            'classificacao_fundiaria',
                            'Classificação fundiária',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="classificacao_fundiaria"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(ccir.classificacao_fundiaria)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'uf_sede') && (
                      <Grid item xs={6} md={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('serpro_ccir', 'uf_sede', 'UF sede')}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="uf_sede"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(ccir.uf_sede)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'municipio_sede') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('serpro_ccir', 'municipio_sede', 'Município sede')}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="municipio_sede"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(ccir.municipio_sede)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'area_total') && (
                      <Grid item xs={12} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('serpro_ccir', 'area_total', 'Área total')}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="area_total"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatAreaHa(ccir.area_total, 2)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('serpro_ccir', 'dados_imovel', 'indicacoes_localizacao') && (
                      <Grid item xs={12} md={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel(
                            'serpro_ccir',
                            'indicacoes_localizacao',
                            'Indicações de localização',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="indicacoes_localizacao"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(ccir.indicacoes_localizacao)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled(
                      'serpro_ccir',
                      'dados_imovel',
                      'data_processamento_ultima_declaracao',
                    ) && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel(
                            'serpro_ccir',
                            'data_processamento_ultima_declaracao',
                            'Data processamento última declaração',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="serpro_ccir"
                          fieldKey="data_processamento_ultima_declaracao"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatDateTimeBr(ccir.data_processamento_ultima_declaracao) || '-'}
                        </ReportStyledField>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {isSectionEnabled('serpro_ccir', 'estrutura_fundiaria') && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Typography variant="subtitle2">Estrutura fundiária</Typography>
                        {ccir.classificacao_fundiaria && (
                          <Chip size="small" color="info" label={ccir.classificacao_fundiaria} />
                        )}
                      </Box>
                      <Grid container spacing={1.5}>
                        {isFieldEnabled(
                          'serpro_ccir',
                          'estrutura_fundiaria',
                          'area_certificada',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'area_certificada', 'Área certificada')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="area_certificada"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.area_certificada, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'estrutura_fundiaria',
                          'total_area_registrada',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'total_area_registrada',
                                'Total área registrada',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="total_area_registrada"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.total_area_registrada, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'estrutura_fundiaria',
                          'total_area_posse_justo_titulo',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'total_area_posse_justo_titulo',
                                'Total área posse (justo título)',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="total_area_posse_justo_titulo"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.total_area_posse_justo_titulo, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'estrutura_fundiaria',
                          'total_area_posse_simples_ocupacao',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'total_area_posse_simples_ocupacao',
                                'Total área posse (simples ocupação)',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="total_area_posse_simples_ocupacao"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.total_area_posse_simples_ocupacao, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'estrutura_fundiaria', 'area_medida') && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'area_medida', 'Área medida')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="area_medida"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.area_medida, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'estrutura_fundiaria',
                          'total_pessoas_relacionadas_imovel',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'total_pessoas_relacionadas_imovel',
                                'Total de pessoas relacionadas',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="total_pessoas_relacionadas_imovel"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(ccir.total_pessoas_relacionadas_imovel)}
                            </ReportStyledField>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                {isSectionEnabled('serpro_ccir', 'modulos_parametros') && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        Módulos e parâmetros
                      </Typography>
                      <Grid container spacing={1.5}>
                        {isFieldEnabled(
                          'serpro_ccir',
                          'modulos_parametros',
                          'area_modulo_rural',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'area_modulo_rural',
                                'Área módulo rural',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="area_modulo_rural"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.area_modulo_rural, 4)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'modulos_parametros',
                          'numero_modulos_rurais',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'numero_modulos_rurais',
                                'Nº módulos rurais',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="numero_modulos_rurais"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(ccir.numero_modulos_rurais)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'modulos_parametros',
                          'area_modulo_fiscal',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'area_modulo_fiscal',
                                'Área módulo fiscal',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="area_modulo_fiscal"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.area_modulo_fiscal, 4)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'modulos_parametros',
                          'numero_modulos_fiscais',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'numero_modulos_fiscais',
                                'Nº módulos fiscais',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="numero_modulos_fiscais"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(ccir.numero_modulos_fiscais)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'modulos_parametros',
                          'fracao_minima_parcelamento',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'fracao_minima_parcelamento',
                                'Fração mínima de parcelamento',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="fracao_minima_parcelamento"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatAreaHa(ccir.fracao_minima_parcelamento, 2)}
                            </ReportStyledField>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Áreas registradas */}
              {isSectionEnabled('serpro_ccir', 'areas_registradas') && (
                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Áreas registradas</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {areasRegistradas.length === 0
                          ? '0 registros'
                          : areasRegistradas.length === 1
                            ? '1 registro'
                            : `${areasRegistradas.length} registros`}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {areasRegistradas.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem dados disponíveis.
                      </Typography>
                    ) : (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data registro</TableCell>
                              <TableCell>Cartório (UF / Município)</TableCell>
                              <TableCell>Matrícula / Registro</TableCell>
                              <TableCell>Livro / Ficha</TableCell>
                              <TableCell align="right">Área (ha)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {areasRegistradas.map((area, idx) => {
                              const ufCartorio = formatGeneric(area?.uf_cartorio);
                              const municipioCartorio = formatGeneric(area?.municipio_cartorio);
                              const cartorioParts = [ufCartorio, municipioCartorio].filter(
                                (part) => part && part !== '-',
                              );
                              const cartorioLabel =
                                cartorioParts.length > 0 ? cartorioParts.join(' / ') : '-';

                              return (
                                <TableRow key={`area-${idx}`}>
                                  <TableCell>{formatDateBr(area?.data_registro) || '-'}</TableCell>
                                  <TableCell>{cartorioLabel}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {formatGeneric(area?.matricula_ou_transcricao)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      {formatGeneric(area?.registro)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{formatGeneric(area?.livro_ou_ficha)}</TableCell>
                                  <TableCell align="right">
                                    {safeNumber(area?.area) === null
                                      ? '-'
                                      : safeNumber(area?.area).toLocaleString('pt-BR', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Titulares */}
              {isSectionEnabled('serpro_ccir', 'titulares') && (
                <Accordion disableGutters sx={{ mt: 1.5 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Titulares</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {titulares.length === 0
                          ? '0 titulares'
                          : titulares.length === 1
                            ? '1 titular'
                            : `${titulares.length} titulares`}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {titulares.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem dados disponíveis.
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {titulares.map((titular, tIndex) => (
                          <Grid item xs={12} md={6} key={tIndex}>
                            <CcirTitularCard titular={titular} />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Dados do último CCIR */}
              {isSectionEnabled('serpro_ccir', 'dados_ultimo_ccir') && (
                <Accordion disableGutters sx={{ mt: 1.5 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>Dados do último CCIR</Typography>
                      {dadosUltimoCcir?.situacao_ccir && (
                        <Chip
                          size="small"
                          color={getStatusChipColor(dadosUltimoCcir.situacao_ccir)}
                          label={dadosUltimoCcir.situacao_ccir}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {!dadosUltimoCcir ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem dados disponíveis.
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'data_lancamento') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'data_lancamento',
                                'Data de lançamento',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="data_lancamento"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatDateBr(dadosUltimoCcir.data_lancamento) || '-'}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'numero_ccir') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'numero_ccir', 'Número CCIR')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="numero_ccir"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(dadosUltimoCcir.numero_ccir)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'dados_ultimo_ccir',
                          'numero_autenticidade_ccir',
                        ) && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'numero_autenticidade_ccir',
                                'Número de autenticidade',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="numero_autenticidade_ccir"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(dadosUltimoCcir.numero_autenticidade_ccir)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'dados_ultimo_ccir',
                          'data_geracao_ccir',
                        ) && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'data_geracao_ccir', 'Data de geração')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="data_geracao_ccir"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatDateTimeBr(dadosUltimoCcir.data_geracao_ccir) || '-'}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'dados_ultimo_ccir',
                          'data_vencimento_ccir',
                        ) && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'data_vencimento_ccir',
                                'Data de vencimento',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="data_vencimento_ccir"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatDateBr(dadosUltimoCcir.data_vencimento_ccir) || '-'}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'dados_ultimo_ccir',
                          'debitos_anteriores',
                        ) && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'debitos_anteriores',
                                'Débitos anteriores',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="debitos_anteriores"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.debitos_anteriores)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'serpro_ccir',
                          'dados_ultimo_ccir',
                          'taxa_servicos_cadastrais',
                        ) && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'serpro_ccir',
                                'taxa_servicos_cadastrais',
                                'Taxa de serviços',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="taxa_servicos_cadastrais"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.taxa_servicos_cadastrais)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'valor_cobrado') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'valor_cobrado', 'Valor cobrado')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="valor_cobrado"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.valor_cobrado)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'multa') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'multa', 'Multa')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="multa"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.multa)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'juros') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'juros', 'Juros')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="juros"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.juros)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('serpro_ccir', 'dados_ultimo_ccir', 'valor_total') && (
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel('serpro_ccir', 'valor_total', 'Valor total')}
                            </Typography>
                            <ReportStyledField
                              domainKey="serpro_ccir"
                              fieldKey="valor_total"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatCurrencyBr(dadosUltimoCcir.valor_total)}
                            </ReportStyledField>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

function CcirTitularCard({ titular }) {
  if (!titular || typeof titular !== 'object') return null;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isFieldEnabled, getFieldLabel } = useReportConfig();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {formatGeneric(titular.nome_titular) || 'Titular'}
        </Typography>
        <Grid container spacing={1}>
          {isFieldEnabled('serpro_ccir', 'titulares', 'cpf_cnpj') && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('serpro_ccir', 'cpf_cnpj', 'CPF/CNPJ')}
              </Typography>
              <ReportStyledField
                domainKey="serpro_ccir"
                fieldKey="cpf_cnpj"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatDocument(titular.cpf_cnpj)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('serpro_ccir', 'titulares', 'declarante') && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('serpro_ccir', 'declarante', 'Declarante')}
              </Typography>
              <ReportStyledField
                domainKey="serpro_ccir"
                fieldKey="declarante"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular.declarante)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('serpro_ccir', 'titulares', 'condicao_titularidade') && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('serpro_ccir', 'condicao_titularidade', 'Condição de titularidade')}
              </Typography>
              <ReportStyledField
                domainKey="serpro_ccir"
                fieldKey="condicao_titularidade"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular.condicao_titularidade)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('serpro_ccir', 'titulares', 'percentual_detencao') && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('serpro_ccir', 'percentual_detencao', 'Percentual de detenção')}
              </Typography>
              <ReportStyledField
                domainKey="serpro_ccir"
                fieldKey="percentual_detencao"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatPercent(titular.percentual_detencao)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('serpro_ccir', 'titulares', 'nacionalidade') && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('serpro_ccir', 'nacionalidade', 'Nacionalidade')}
              </Typography>
              <ReportStyledField
                domainKey="serpro_ccir"
                fieldKey="nacionalidade"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular.nacionalidade)}
              </ReportStyledField>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}
